import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import SearchableCombobox from '@/components/ui/searchable-combobox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Reusable Marca + Modelo selector with searchable combos and quick-add buttons.
 *
 * Props:
 *   marca       - current marca name (string)
 *   modelo      - current modelo name (string)
 *   onMarcaChange(nombre)  - callback when marca changes
 *   onModeloChange(nombre) - callback when modelo changes
 *   errors      - { marca, modelo } error messages
 *   disabled    - boolean
 */
const MarcaModeloSelector = ({ marca, modelo, onMarcaChange, onModeloChange, errors = {}, disabled = false }) => {
  const [marcas, setMarcas] = useState([]);
  const [modelos, setModelos] = useState([]);

  // Quick-add modals
  const [showAddMarca, setShowAddMarca] = useState(false);
  const [showAddModelo, setShowAddModelo] = useState(false);
  const [newMarcaNombre, setNewMarcaNombre] = useState('');
  const [newModeloNombre, setNewModeloNombre] = useState('');
  const [addingMarca, setAddingMarca] = useState(false);
  const [addingModelo, setAddingModelo] = useState(false);

  // Track the selected marca object to get its ID for loading models
  const [selectedMarcaObj, setSelectedMarcaObj] = useState(null);

  // Load marcas on mount
  useEffect(() => {
    loadMarcas();
  }, []);

  // When marca prop changes, find its object and load models
  useEffect(() => {
    if (marca && marcas.length > 0) {
      const found = marcas.find(m => m.nombre.toLowerCase() === marca.toLowerCase());
      if (found) {
        setSelectedMarcaObj(found);
        loadModelos(found.id);
      } else {
        setSelectedMarcaObj(null);
        setModelos([]);
      }
    } else {
      setSelectedMarcaObj(null);
      setModelos([]);
    }
  }, [marca, marcas]);

  const loadMarcas = async () => {
    try {
      const response = await axios.get(`${API}/marcas`);
      setMarcas(response.data);
    } catch (error) {
      console.error('Error loading marcas:', error);
    }
  };

  const loadModelos = async (marcaId) => {
    try {
      const response = await axios.get(`${API}/marcas/${marcaId}/modelos`);
      setModelos(response.data);
    } catch (error) {
      console.error('Error loading modelos:', error);
    }
  };

  const handleMarcaSelect = (marcaNombre) => {
    onMarcaChange(marcaNombre);
    // Clear modelo when marca changes
    if (marcaNombre.toLowerCase() !== marca?.toLowerCase()) {
      onModeloChange('');
    }
  };

  const handleModeloSelect = (modeloNombre) => {
    onModeloChange(modeloNombre);
  };

  const handleAddMarca = async () => {
    if (!newMarcaNombre.trim()) {
      toast.error('El nombre de la marca es requerido');
      return;
    }
    setAddingMarca(true);
    try {
      const response = await axios.post(`${API}/marcas`, { nombre: newMarcaNombre.trim() });
      toast.success(`Marca "${response.data.nombre}" creada`);
      setShowAddMarca(false);
      setNewMarcaNombre('');
      await loadMarcas();
      // Auto-select the new marca
      onMarcaChange(response.data.nombre);
      onModeloChange('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear marca');
    } finally {
      setAddingMarca(false);
    }
  };

  const handleAddModelo = async () => {
    if (!newModeloNombre.trim()) {
      toast.error('El nombre del modelo es requerido');
      return;
    }
    if (!selectedMarcaObj) {
      toast.error('Primero seleccione una marca');
      return;
    }
    setAddingModelo(true);
    try {
      const response = await axios.post(`${API}/modelos`, {
        marcaId: selectedMarcaObj.id,
        nombre: newModeloNombre.trim()
      });
      toast.success(`Modelo "${response.data.nombre}" creado`);
      setShowAddModelo(false);
      setNewModeloNombre('');
      await loadModelos(selectedMarcaObj.id);
      // Auto-select the new modelo
      onModeloChange(response.data.nombre);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear modelo');
    } finally {
      setAddingModelo(false);
    }
  };

  const marcaOptions = marcas.map(m => ({
    value: m.nombre,
    label: m.nombre,
  }));

  const modeloOptions = modelos.map(m => ({
    value: m.nombre,
    label: m.nombre,
  }));

  return (
    <>
      <div className="grid sm:grid-cols-2 gap-4">
        {/* Marca Combobox */}
        <div className="space-y-2">
          <Label>Marca *</Label>
          <div className="flex gap-2">
            <SearchableCombobox
              value={marca}
              onSelect={handleMarcaSelect}
              options={marcaOptions}
              placeholder="Seleccionar marca..."
              searchPlaceholder="Buscar marca..."
              emptyText="No se encontró la marca."
              disabled={disabled}
              data-testid="marca-combobox"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => { setNewMarcaNombre(''); setShowAddMarca(true); }}
              disabled={disabled}
              title="Agregar nueva marca"
              className="shrink-0"
              data-testid="add-marca-button"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          {errors.marca && (
            <p className="text-sm text-destructive">{errors.marca}</p>
          )}
        </div>

        {/* Modelo Combobox */}
        <div className="space-y-2">
          <Label>Modelo *</Label>
          <div className="flex gap-2">
            <SearchableCombobox
              value={modelo}
              onSelect={handleModeloSelect}
              options={modeloOptions}
              placeholder={marca ? "Seleccionar modelo..." : "Primero seleccione marca"}
              searchPlaceholder="Buscar modelo..."
              emptyText="No se encontró el modelo."
              disabled={disabled || !marca}
              data-testid="modelo-combobox"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => { setNewModeloNombre(''); setShowAddModelo(true); }}
              disabled={disabled || !selectedMarcaObj}
              title="Agregar nuevo modelo"
              className="shrink-0"
              data-testid="add-modelo-button"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          {errors.modelo && (
            <p className="text-sm text-destructive">{errors.modelo}</p>
          )}
        </div>
      </div>

      {/* Quick-add Marca Dialog */}
      <Dialog open={showAddMarca} onOpenChange={setShowAddMarca}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Nueva Marca</DialogTitle>
            <DialogDescription>Ingrese el nombre de la marca</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={newMarcaNombre}
              onChange={(e) => setNewMarcaNombre(e.target.value)}
              placeholder="Ej. Toyota"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleAddMarca()}
              data-testid="new-marca-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMarca(false)} disabled={addingMarca}>
              Cancelar
            </Button>
            <Button onClick={handleAddMarca} disabled={addingMarca} data-testid="save-new-marca-button">
              {addingMarca ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick-add Modelo Dialog */}
      <Dialog open={showAddModelo} onOpenChange={setShowAddModelo}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Nuevo Modelo</DialogTitle>
            <DialogDescription>
              Agregar modelo para {selectedMarcaObj?.nombre || marca}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={newModeloNombre}
              onChange={(e) => setNewModeloNombre(e.target.value)}
              placeholder="Ej. Corolla"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleAddModelo()}
              data-testid="new-modelo-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModelo(false)} disabled={addingModelo}>
              Cancelar
            </Button>
            <Button onClick={handleAddModelo} disabled={addingModelo} data-testid="save-new-modelo-button">
              {addingModelo ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MarcaModeloSelector;
